import { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  alpha,
  useTheme,
  Collapse,
} from "@mui/material";
import { ChevronDown, ChevronUp, StickyNote, Check, Loader2 } from "lucide-react";
import { updateMarketingOrganization } from "../../../../../../../redux/actions/marketingAction";

const FicheNotes = ({ org, orgId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(true);
  const [notes, setNotes] = useState(org.notes || "");
  const [saveState, setSaveState] = useState("idle"); // idle, saving, saved
  const timerRef = useRef(null);
  const lastSavedRef = useRef(org.notes || "");

  useEffect(() => {
    setNotes(org.notes || "");
    lastSavedRef.current = org.notes || "";
  }, [org.notes]);

  const saveNotes = useCallback(async (value) => {
    if (value === lastSavedRef.current) return;
    setSaveState("saving");
    try {
      await dispatch(updateMarketingOrganization(orgId, { notes: value }));
      lastSavedRef.current = value;
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2000);
    } catch {
      setSaveState("idle");
    }
  }, [dispatch, orgId]);

  const handleChange = (e) => {
    const value = e.target.value;
    setNotes(value);
    setSaveState("idle");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveNotes(value), 1000);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <Box>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.5, py: 1, cursor: "pointer",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 1, color: theme.palette.text.secondary }}>
          Notes
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {saveState === "saving" && (
            <Typography variant="caption" sx={{ fontSize: "0.6rem", color: theme.palette.text.disabled, display: "flex", alignItems: "center", gap: 0.5 }}>
              <Loader2 size={10} className="animate-spin" /> Saving...
            </Typography>
          )}
          {saveState === "saved" && (
            <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "#10b981", display: "flex", alignItems: "center", gap: 0.5 }}>
              <Check size={10} /> Saved
            </Typography>
          )}
          {expanded ? <ChevronUp size={14} color={theme.palette.text.disabled} /> : <ChevronDown size={14} color={theme.palette.text.disabled} />}
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 2.5, pb: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={notes}
            onChange={handleChange}
            placeholder="Notes internes..."
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.8rem",
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.background.default, 0.5),
              },
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default FicheNotes;
